// dependencies
import React from "react";
import { SWRConfig } from "swr";

// containers
import { Room as RoomContainer } from "../../containers/Room";

// constants
import { searchDefaultParameters } from "../../constants/search";
import { ROOM_COMMENT_PER_PAGE } from "../../constants/pages";

// helpers
import {
  roomIsNatureChild,
  roomIsNatureParent,
  roomInChannel,
  roomIsStateEnabled,
  roomIsStateDisabled,
} from "@cloudspire/legacy-resources/src/helpers/room";
import { nextRedirect } from "@cloudspire/legacy-resources/src/helpers/next";
import bookingDelegationGetChannelId from "@cloudspire/legacy-resources/src/helpers/booking/delegation/getChannelId";

// libraries
import { generateUri } from "@cloudspire/legacy-shared/src/libraries";
import { fetchFallback } from "@cloudspire/legacy-resources/src/libraries/utils/fetcher";

// errors
import { RoomNotFoundError } from "@cloudspire/legacy-resources/src/errors";

// api
import { roomRoomPage } from "@cloudspire/legacy-resources/src/api/room";
import { commentsRoomPage } from "@cloudspire/legacy-resources/src/api/comment";
import { pricingEstimateRoomPage } from "@cloudspire/legacy-resources/src/api/pricing";

function RoomPage(props) {
  const { roomReference, roomSlug, fallback } = props;

  return (
    <SWRConfig value={{ fallback }}>
      <RoomContainer roomReference={roomReference} roomSlug={roomSlug} />
    </SWRConfig>
  );
}

RoomPage.getInitialProps = async (ctx) => {
  const { route, res, language, configuration, routers, isomorphicFetcher } =
    ctx;

  const { frontRouter } = routers;

  if (!route) {
    throw new RoomNotFoundError("", {});
  }

  const routeName = route.getName();
  const query = route.getUri().getParsedQuery();

  let referenceOrSlug;
  switch (routeName) {
    case "App.Action.RoomWithRef": {
      referenceOrSlug = route.getParameter("reference");
      break;
    }

    case "App.Action.RoomWithSlug":
    default: {
      referenceOrSlug = route.getParameter("slug");
      break;
    }
  }

  const [roomPageRoomFallback, roomShow] = await fetchFallback({
    ...roomRoomPage({
      referenceOrSlug: referenceOrSlug,
      checkin: query?.checkin,
      configuration,
    }),
    fetcher: isomorphicFetcher,
  }).catch(() => {
    throw new RoomNotFoundError("", {});
  });

  const roomSlug = roomShow.data.slug;

  if (
    // Si le slug de l’URL est différent du slug de l’hébergement.
    decodeURIComponent(referenceOrSlug) !== roomSlug
  ) {
    const uri = generateUri({
      router: frontRouter,
      name: "App.Action.RoomWithSlug",
      parameters: {
        lang: language,
        slug: roomShow.data.slug,
      },
      query,
    });

    return await nextRedirect({ redirectionUri: uri, code: 301, res });
  }

  if (roomIsNatureChild({ room: roomShow.data })) {
    // Si c'est un enfant, on redirige vers la structure parente.

    const uri = generateUri({
      router: frontRouter,
      name: "App.Action.RoomWithSlug",
      parameters: {
        lang: language,
        slug: roomShow.data.parentReference,
      },
      query,
    });

    return await nextRedirect({ redirectionUri: uri, code: 302, res });
  }

  if (
    !roomInChannel({
      room: roomShow.data,
      channelIdList: configuration.channel.children,
    })
  ) {
    throw new RoomNotFoundError("", {});
  }

  if (
    /**
     * Si l’hébergement est activé ou désactivé, on l’affiche.
     * S’il est dans un autre état (supprimé par exemple), on ne l’affiche pas.
     */
    !(
      roomIsStateEnabled({ room: roomShow.data }) ||
      roomIsStateDisabled({ room: roomShow.data })
    )
  ) {
    throw new RoomNotFoundError("", {});
  }

  let reference = roomShow.data?.reference;
  let slug = roomShow.data?.slug;
  const roomReferenceList = [reference];

  if (roomIsNatureParent({ room: roomShow.data })) {
    for (const roomChild of roomShow.data?.children ?? []) {
      roomReferenceList.push(roomChild.reference);
    }
  }

  const [[roomPagePricingEstimateFallback], [roomPageCommentFallback]] =
    await Promise.all([
      fetchFallback({
        ...pricingEstimateRoomPage({
          roomReferenceList,
          channelId: bookingDelegationGetChannelId({
            configuration,
            room: roomShow.data,
          }),
        }),
        fetcher: isomorphicFetcher,
      }),
      fetchFallback({
        ...commentsRoomPage({
          roomReference: reference,
          query: {
            ...searchDefaultParameters,
            per_page: ROOM_COMMENT_PER_PAGE,
            page: 1,
          },
        }),
        fetcher: isomorphicFetcher,
      }),
    ]).catch(() => {
      throw new RoomNotFoundError("", {});
    });

  return {
    fallback: {
      ...roomPageRoomFallback,
      ...roomPagePricingEstimateFallback,
      ...roomPageCommentFallback,
    },
    roomReference: reference,
    roomSlug: slug,
  };
};

export default RoomPage;
